import { Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import {
  getBuildingInfo,
  getEstimatedReplacementCost,
  type PrometrixBuildingInfo,
} from 'api/prometrix';
import {
  type ExposureOrGroup,
  type ObjectLiteral,
  useSubmission,
  useUnderwritingQuestions,
} from 'api/submission';
import {
  APPLICATION_STEPS,
  DEFAULT_MAX_STORIES_NUM,
  DEFAULT_MIN_STORIES_NUM,
  duplicatedBuildingNameErrorMessage,
  IS_OUTBUILDING_FIELD_CODE,
  IS_SPRINKLER_INSPECTED_ANNUALLY_FIELD_CODE,
  PROPERTIES_UW_EXCLUDED_FIELD_CODES,
  propertyExposureAffectPremiumCalculationsFields,
  SPRINKLER_INSPECTION_ACKNOWLEDGEMENT_FIELD_CODE,
} from 'common/constants';
import { Modal } from 'components/Modal';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import {
  QuestionEngine,
  type QuestionEngineOnChangeProps,
  useQuestionEngine,
} from 'components/lib/QuestionEngine';
import { setBdgPrometrixHitValue } from 'helpers/utils';
import { isEmpty, isEqual, omit, pick } from 'lodash-es';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';
import * as yup from 'yup';
import type { IProperty } from '../../Properties.types';
import type { PropertyModalProps, PropertyModalRef } from './PropertyModal.types';

type TExposure = ExposureOrGroup<IProperty>;

const { fields } = APPLICATION_STEPS.properties;

const propertyExposureScheduleBuildingOccupanciesKeyValue = {
  BUILDING_LEASE: `Building I lease and am required to insure (e.g. 'Triple Net Lease')`,
  BUILDING_OWN_RUN: 'Building I own and run my business out of',
  BUILDING_OWN_MANAGE: 'Building I own/manage and lease to others',
};

const propertyExposureScheduleBuildingOccupancies = Object.values(
  propertyExposureScheduleBuildingOccupanciesKeyValue,
);

const propertyExposureScheduleBuildingFields = [
  'bdg_erc_building',
  'bdg_rrc_building',
  'bdg_tiv_building',
  'bdg_360v_valuation_id',
];

const propertiesUwQuestionCode = 'premise_details';

const PropertyModal = forwardRef<PropertyModalRef | undefined, PropertyModalProps>(
  ({ handleAdd, handleUpdate, currentLocation, policyData, properties }, ref) => {
    const { id } = useParams() as { id: string };
    const { data: submission } = useSubmission(id);

    const [editingExposure, setEditingExposure] = useState<TExposure>({} as TExposure);
    const [open, setOpen] = useState(false);
    const [initialIntegrationResult, setInitialIntegrationResult] =
      useState<PrometrixBuildingInfo>();
    const isEditing = !isEmpty(editingExposure);
    const title = `${isEditing ? 'Edit' : 'Add'} property`;
    const { data: questions } = useUnderwritingQuestions(id, true);

    const { setFullscreenLoading } = useFullscreenLoader();

    const { pol_tax_cf_360v, pol_tax_cf_rule, pol_tax_cf_default } = policyData || {};
    const classCode = pol_tax_cf_rule || pol_tax_cf_default;

    const propertiesName = useMemo(
      () => properties?.map((property) => property?.data?.bdg_name?.toLowerCase()?.trim()) || [],
      [properties],
    );

    const isOutbuildingQuestionPresent = useMemo(() => {
      const propertiesUWQuestionSection = questions?.allQuestions.filter(
        (q) => q.code === propertiesUwQuestionCode,
      );

      return propertiesUWQuestionSection
        ?.at(0)
        ?.fields?.at(0)
        ?.nested_fields?.find((f) => f.code === IS_OUTBUILDING_FIELD_CODE);
    }, [questions]);

    const propertiesQuestionFields = useMemo(() => {
      const propertiesUWQuestionSection = questions?.allQuestions.filter(
        (q) => q.code === propertiesUwQuestionCode,
      );

      return (
        propertiesUWQuestionSection
          ?.at(0)
          ?.fields?.at(0)
          ?.nested_fields?.filter((f) => !PROPERTIES_UW_EXCLUDED_FIELD_CODES.includes(f.code)) || []
      );
    }, [questions]);

    const customValidation = {
      bdg_name: yup
        .string()
        .test('unique per location', duplicatedBuildingNameErrorMessage, (value = '') => {
          const transformedValue = value ? value?.toLowerCase()?.trim() : '';

          if (
            !isEditing ||
            editingExposure?.data?.bdg_name?.toLowerCase()?.trim() !== transformedValue
          ) {
            return !propertiesName?.some((s) => transformedValue === s) as boolean;
          } else {
            return propertiesName?.filter((s) => transformedValue === s).length <= 1;
          }
        })
        .required(),
      bdg_num_stories: yup
        .number()
        .integer('This value cannot be a decimal number.')
        .required()
        .min(
          Number(DEFAULT_MIN_STORIES_NUM),
          `The value must be at least ${DEFAULT_MIN_STORIES_NUM}.`,
        )
        .max(
          Number(DEFAULT_MAX_STORIES_NUM),
          `The value must be no more than ${DEFAULT_MAX_STORIES_NUM}.`,
        ),
    };

    const { validateFormik: validatePropertiesUWQuestionFormik, ...propertiesQuestionEngineProps } =
      useQuestionEngine<ObjectLiteral>({ fields: propertiesQuestionFields });

    const {
      formik: propertiesUWFormik,
      state: propertiesUWState,
      setState: setPropertiesUWQuestionState,
    } = propertiesQuestionEngineProps;

    const modifiedPropertiesUWState = useMemo(
      () => ({
        ...propertiesUWState,
        ...(isOutbuildingQuestionPresent && { [IS_OUTBUILDING_FIELD_CODE]: 'No' }),
      }),
      [propertiesUWState, isOutbuildingQuestionPresent],
    );

    const { validateFormik, ...questionEngineProps } = useQuestionEngine<IProperty>({
      fields,
      customValidation,
    });

    const { formik, setState, state } = questionEngineProps;

    const isIntegrationResultsChanged = propertyExposureAffectPremiumCalculationsFields.some(
      (fieldName) => String(state?.[fieldName]) !== String(initialIntegrationResult?.[fieldName]),
    );

    const { mutateAsync: getPrometrixBuildingInfo } = useMutation({
      mutationFn: getBuildingInfo,
      onMutate: () => {
        setFullscreenLoading(true, 'Fetching the building information');
      },
      onSettled: () => {
        setFullscreenLoading(false);
      },
      onError: (_error) => {
        setState?.((prevState) => ({
          ...prevState,
          bdg_protection_class: 5,
        }));
      },
    });

    useImperativeHandle(ref, () => ({
      openModal: (exposure) => {
        if (exposure) {
          setEditingExposure(exposure);

          // Only load sprinkler questions if sprinkler system is "Yes"
          const isSprinklerSystemEnabled = exposure?.data?.bdg_issprinklered === 'Yes';
          const sprinklerQuestions = isSprinklerSystemEnabled
            ? {
                [IS_SPRINKLER_INSPECTED_ANNUALLY_FIELD_CODE]:
                  exposure?.data?.underwriting_question?.[
                    IS_SPRINKLER_INSPECTED_ANNUALLY_FIELD_CODE
                  ],
                [SPRINKLER_INSPECTION_ACKNOWLEDGEMENT_FIELD_CODE]:
                  exposure?.data?.underwriting_question?.[
                    SPRINKLER_INSPECTION_ACKNOWLEDGEMENT_FIELD_CODE
                  ],
              }
            : {};

          setState?.({
            ...exposure.data,
            ...sprinklerQuestions,
          });
          formik?.setValues({
            ...exposure.data,
            ...sprinklerQuestions,
          });
          propertiesUWFormik.setValues(exposure?.data?.underwriting_question || {});
          setPropertiesUWQuestionState?.(exposure?.data?.underwriting_question || {});
        }

        setOpen(true);
      },
    }));

    const resetModal = () => {
      setOpen(false);
      formik?.resetForm();
      setState?.({} as IProperty);
      setEditingExposure({} as TExposure);
      propertiesUWFormik?.resetForm();
      setPropertiesUWQuestionState?.({});
    };

    // eslint-disable-next-line complexity
    const handleAction = async () => {
      const isValid = await validateFormik();
      const isUWQuestionsValid = await validatePropertiesUWQuestionFormik();

      if (!isValid || !isUWQuestionsValid) {
        return false;
      }

      let estimatedCostResponse: ObjectLiteral = {};

      if (
        propertyExposureScheduleBuildingOccupancies.includes(formik.values?.bdg_occupancy) &&
        !isEqual(editingExposure.data, formik.values)
      ) {
        try {
          setFullscreenLoading(
            true,
            isEditing
              ? `Updating the ${formik.values.bdg_name}`
              : `Adding ${formik.values.bdg_name}`,
          );

          estimatedCostResponse = await getEstimatedReplacementCost({
            occupancy_use: pol_tax_cf_360v ? String(pol_tax_cf_360v) : '',
            square_footage_total: state?.bdg_sqft!,
            year_built: state?.bdg_year_built!,
            construction_class: state?.bdg_construction_code!,
            address: {
              street_name: currentLocation?.loc_address_destructured?.street_line!,
              city: currentLocation?.loc_address_destructured?.city!,
              state: currentLocation?.loc_address_destructured?.state!,
              zipcode: currentLocation?.loc_address_destructured?.zipcode!,
            },
            consumer_submission_id: id,

            // unused, but kept for posterity
            csp_occupancy_code: classCode,
            num_stories: state?.bdg_num_stories!,
            square_footage_occupied: state?.bdg_sqft,
            has_sprinklers: state?.bdg_issprinklered === 'Yes',
          });

          estimatedCostResponse.bdg_tiv_building = estimatedCostResponse.bdg_erc_building;

          setState?.((prevState) => ({
            ...prevState,
            ...estimatedCostResponse,
          }));
        } catch (_error) {
          // do nothing
        }
      }

      // Only include sprinkler questions in underwriting_question if sprinkler system is "Yes"
      const isSprinklerSystemEnabled = formik.values?.bdg_issprinklered === 'Yes';
      const sprinklerAnswers = isSprinklerSystemEnabled
        ? {
            [IS_SPRINKLER_INSPECTED_ANNUALLY_FIELD_CODE]:
              formik?.values?.[IS_SPRINKLER_INSPECTED_ANNUALLY_FIELD_CODE],
            [SPRINKLER_INSPECTION_ACKNOWLEDGEMENT_FIELD_CODE]:
              formik?.values?.[SPRINKLER_INSPECTION_ACKNOWLEDGEMENT_FIELD_CODE],
          }
        : {};

      const data = {
        ...omit(formik.values, [
          IS_SPRINKLER_INSPECTED_ANNUALLY_FIELD_CODE,
          SPRINKLER_INSPECTION_ACKNOWLEDGEMENT_FIELD_CODE,
        ]),
        ...estimatedCostResponse,
        underwriting_question: {
          ...modifiedPropertiesUWState,
          ...sprinklerAnswers,
        },
      } as any as IProperty;

      if (isEditing) {
        await handleUpdate(data, editingExposure?.id);
      } else {
        await handleAdd(data);
      }

      resetModal();
      return true;
    };

    useEffect(() => {
      formik.setValues(state ?? ({} as IProperty));
    }, [state]);

    useEffect(() => {
      propertiesUWFormik.setValues(modifiedPropertiesUWState);
    }, [modifiedPropertiesUWState]);

    const bdgPrometrixHit = useMemo(
      () => setBdgPrometrixHitValue(state, initialIntegrationResult, isIntegrationResultsChanged),
      [state, initialIntegrationResult, isIntegrationResultsChanged],
    );

    useEffect(() => {
      setState?.((prevState) =>
        bdgPrometrixHit
          ? { ...prevState, bdg_prometrix_hit: bdgPrometrixHit }
          : (omit(prevState, 'bdg_prometrix_hit') as IProperty),
      );
    }, [bdgPrometrixHit]);

    const callPrometrixForUWreport = async (
      occupancy: string,
      onlyChangePPC = false,
      isInitEditCall = false,
    ) => {
      const {
        street_line,
        city,
        state: locationState,
        zipcode,
      } = currentLocation?.loc_address_destructured || {};

      const buildingInfoResponse = await getPrometrixBuildingInfo({
        address: {
          street_name: street_line!,
          city: city!,
          state: locationState!,
          zipcode: zipcode!,
        },
        occupancy_use: pol_tax_cf_360v ? String(pol_tax_cf_360v) : '',
        fetch_replacement_costs: propertyExposureScheduleBuildingOccupancies.includes(occupancy),
        consumer_submission_id: id,
      });

      setState?.((prevState) => {
        const tmpRes = isInitEditCall
          ? {}
          : onlyChangePPC
            ? pick(buildingInfoResponse, ['bdg_protection_class'])
            : { ...buildingInfoResponse };

        return {
          ...prevState,
          // if rated replacement was edited do not change it
          ...(prevState.bdg_erc_building !== prevState.bdg_rrc_building
            ? omit(tmpRes, 'bdg_rrc_building')
            : tmpRes),
          bdg_tiv_building: buildingInfoResponse.bdg_erc_building,
        };
      });

      setInitialIntegrationResult(buildingInfoResponse);
    };

    useEffect(() => {
      if (
        isEditing &&
        state != null &&
        propertyExposureScheduleBuildingOccupancies.includes(state.bdg_occupancy)
      ) {
        callPrometrixForUWreport(state.bdg_occupancy, true);
      }
    }, [isEditing]);

    const onChangeEmitted = async (data: QuestionEngineOnChangeProps) => {
      const { field, value, oldValue } = data || {};

      switch (field) {
        case 'bdg_occupancy': {
          if (!propertyExposureScheduleBuildingOccupancies.includes(value as string)) {
            // clear related field values
            setState?.((prevState) => ({
              ...prevState,
              ...propertyExposureScheduleBuildingFields.reduce(
                (a, b) => ({ ...a, [`${b}`]: '' }),
                {},
              ),
            }));
          }

          switch (value) {
            case 'Home/Apartment':
            case 'Kiosk': {
              const defaultData = {
                bdg_num_stories: 1,
                bdg_issprinklered: 'No',
                bdg_sqft: 100,
                bdg_construction_code: '1',
                bdg_wind_symbol: 'B',
                bdg_year_built: '',
              };

              setState?.((prevState) => ({
                ...prevState,
                ...defaultData,
              }));

              await callPrometrixForUWreport(value, true);

              break;
            }

            case propertyExposureScheduleBuildingOccupanciesKeyValue.BUILDING_OWN_MANAGE:
            case propertyExposureScheduleBuildingOccupanciesKeyValue.BUILDING_LEASE:
            case propertyExposureScheduleBuildingOccupanciesKeyValue.BUILDING_OWN_RUN: {
              if (!propertyExposureScheduleBuildingOccupancies.includes(oldValue as string)) {
                await callPrometrixForUWreport(value as string);
              }

              break;
            }

            default: {
              await callPrometrixForUWreport(value as string);
              break;
            }
          }
          break;
        }

        default:
          break;
      }
    };

    return (
      <Modal
        open={open}
        title={title}
        onClose={resetModal}
        onCloseIconClick={() => resetModal()}
        modalContent={
          <Stack spacing={4}>
            <QuestionEngine {...questionEngineProps} onChangeEmitted={onChangeEmitted} />
            <QuestionEngine
              {...propertiesQuestionEngineProps}
              state={{
                // `effective_date` lives at the top level of the submission (not in
                // `policy_data`) and feeds the `buildingAge` alias the historic-registry
                // show criteria read (ENG-12242). Passed through `state` only — never
                // persisted into `underwriting_question`.
                effective_date: submission?.effective_date,
                ...formik.values,
                ...modifiedPropertiesUWState,
                ...submission?.data?.policy_data,
                ...submission?.data?.uw_questions,
              }}
            />
          </Stack>
        }
        modalFooter={
          <PrimaryButton text={title} fullWidth onClick={handleAction} data-test="add-property" />
        }
      />
    );
  },
);

export default PropertyModal;
